import {
  IX3_WF_EXTENSION_KEYS,
  getMouseFollowConfig,
  getSingleAxisMouseFollowMode,
  type MouseFollowActionConfig,
  type MouseFollowAxis,
} from './types';

type ActionWithMutableProperties = {
  properties: Record<string, unknown>;
};

type ActionWithMouseFollowId = ActionWithMutableProperties & {
  id: string;
};

type TimelineWithMouseFollowActions<TAction extends ActionWithMouseFollowId> = {
  actions?: TAction[];
};

type MouseFollowGroupFields = {
  axis?: MouseFollowAxis;
  followMode?: MouseFollowActionConfig['followMode'];
};

type RemapMouseFollowActionGroupsOptions<
  TAction extends ActionWithMouseFollowId,
> = {
  generateGroupId: () => string;
  actionIdMap?: Record<string, string>;
  mapAction?: (action: TAction) => TAction;
};

/**
 * Drops live relationship metadata from a Mouse Follow config so reusable
 * artifacts like presets do not retain IDs from the source action instance.
 */
export function stripMouseFollowConfigInstanceIds(
  config: MouseFollowActionConfig
): MouseFollowActionConfig {
  const {groupId: _dropGroupId, syncedActionId: _dropSyncId, ...rest} = config;
  return rest;
}

/**
 * Assigns a logical Mouse Follow group and clears the legacy partner pointer.
 * New writes use `groupId`; `syncedActionId` is read only for compatibility.
 */
function withMouseFollowGroup(
  config: MouseFollowActionConfig,
  groupId: string
): MouseFollowActionConfig {
  return {
    ...stripMouseFollowConfigInstanceIds(config),
    groupId,
  };
}

/**
 * Normalizes a Mouse Follow config onto one logical group for create, edit,
 * load, import, paste, and library-export paths.
 */
export function getGroupedMouseFollowConfig(
  config: MouseFollowActionConfig,
  groupId: string,
  fields?: MouseFollowGroupFields
): MouseFollowActionConfig {
  const nextConfig = withMouseFollowGroup(config, groupId);
  if (fields?.axis !== undefined) {
    nextConfig.axis = fields.axis;
  }
  if (fields?.followMode !== undefined) {
    nextConfig.followMode = fields.followMode;
  }
  return nextConfig;
}

/**
 * Converts a paired projection into a standalone survivor after the partner is
 * deleted, duplicated, or moved away from this axis.
 */
export function getUnpairedMouseFollowConfig(
  config: MouseFollowActionConfig,
  axis = config.axis
): MouseFollowActionConfig {
  const {syncedActionId: _drop, ...rest} = config;
  const nextFollowMode =
    rest.followMode === 'full' && axis
      ? getSingleAxisMouseFollowMode(axis)
      : rest.followMode;
  return {
    ...rest,
    ...(nextFollowMode !== undefined ? {followMode: nextFollowMode} : {}),
  };
}

function updateMouseFollowActionConfig<
  TAction extends ActionWithMutableProperties,
>(
  action: TAction,
  updateConfig: (config: MouseFollowActionConfig) => MouseFollowActionConfig
): TAction {
  const config = getMouseFollowConfig(action);
  if (!config) return action;

  const nextConfig = updateConfig(config);
  if (nextConfig === config) return action;

  return {
    ...action,
    properties: {
      ...action.properties,
      [IX3_WF_EXTENSION_KEYS.MOUSE_FOLLOW]: nextConfig,
    },
  };
}

export function setMouseFollowActionConfig<
  TAction extends ActionWithMutableProperties,
>(action: TAction, config: MouseFollowActionConfig): TAction {
  return {
    ...action,
    properties: {
      ...action.properties,
      [IX3_WF_EXTENSION_KEYS.MOUSE_FOLLOW]: config,
    },
  };
}

export function setGroupedMouseFollowActionConfig<
  TAction extends ActionWithMutableProperties,
>(
  action: TAction,
  config: MouseFollowActionConfig,
  groupId: string,
  fields?: MouseFollowGroupFields
): TAction {
  return setMouseFollowActionConfig(
    action,
    getGroupedMouseFollowConfig(config, groupId, fields)
  );
}

export function getUnpairedMouseFollowAction<
  TAction extends ActionWithMutableProperties,
>(action: TAction, axis?: MouseFollowAxis): TAction {
  return updateMouseFollowActionConfig(action, (config) =>
    getUnpairedMouseFollowConfig(config, axis)
  );
}

export function stripMouseFollowActionInstanceIds<
  TAction extends ActionWithMutableProperties,
>(action: TAction): TAction {
  return updateMouseFollowActionConfig(
    action,
    stripMouseFollowConfigInstanceIds
  );
}

function getLegacyMouseFollowGroupKey(
  sourceActionId: string,
  syncedActionId: string,
  actionIdMap: Record<string, string>
): string {
  if (!actionIdMap[syncedActionId]) {
    return `single:${sourceActionId}`;
  }
  return [sourceActionId, syncedActionId].sort().join(':');
}

function getMouseFollowSourceGroupId(
  sourceActionId: string,
  config: MouseFollowActionConfig,
  actionIdMap: Record<string, string>
): string {
  return (
    config.groupId ??
    (config.syncedActionId
      ? getLegacyMouseFollowGroupKey(
          sourceActionId,
          config.syncedActionId,
          actionIdMap
        )
      : `single:${sourceActionId}`)
  );
}

function createMouseFollowGroupNormalizer<
  TAction extends ActionWithMouseFollowId,
>(
  resolveGroupId: (sourceGroupId: string) => string,
  actionIdMap: Record<string, string>
) {
  const groupIdMap: Record<string, string> = {};

  return (action: TAction, sourceActionId = action.id): TAction =>
    updateMouseFollowActionConfig(action, (config) => {
      const sourceGroupId = getMouseFollowSourceGroupId(
        sourceActionId,
        config,
        actionIdMap
      );
      const groupId =
        groupIdMap[sourceGroupId] ?? resolveGroupId(sourceGroupId);
      groupIdMap[sourceGroupId] = groupId;

      return getGroupedMouseFollowConfig(config, groupId);
    });
}

function createMouseFollowActionGroupRemapper<
  TAction extends ActionWithMouseFollowId,
>(
  sourceActions: readonly TAction[],
  generateGroupId: () => string,
  actionIdMap?: Record<string, string>
) {
  const resolvedActionIdMap =
    actionIdMap ??
    Object.fromEntries(sourceActions.map((action) => [action.id, action.id]));
  const normalizeGroup = createMouseFollowGroupNormalizer<TAction>(
    () => generateGroupId(),
    resolvedActionIdMap
  );

  return (action: TAction, sourceActionId?: string): TAction =>
    normalizeGroup(action, sourceActionId ?? action.id);
}

export function remapMouseFollowActionGroupsInTimelines<
  TAction extends ActionWithMouseFollowId,
  TTimeline extends TimelineWithMouseFollowActions<TAction>,
>(
  timelines: TTimeline[],
  {
    generateGroupId,
    actionIdMap,
    mapAction = (action) => action,
  }: RemapMouseFollowActionGroupsOptions<TAction>
): TTimeline[] {
  const remapMouseFollowGroup = createMouseFollowActionGroupRemapper(
    timelines.flatMap((timeline) => timeline.actions ?? []),
    generateGroupId,
    actionIdMap
  );

  return timelines.map((timeline) => {
    let changed = false;
    const actions = timeline.actions?.map((action) => {
      const sourceActionId = action.id;
      const mappedAction = mapAction(action);
      const nextAction = remapMouseFollowGroup(mappedAction, sourceActionId);
      changed = changed || nextAction !== action;
      return nextAction;
    });

    return changed && actions ? {...timeline, actions} : timeline;
  });
}

export function createLoadedMouseFollowActionNormalizer<
  TAction extends ActionWithMouseFollowId,
>(actions: readonly TAction[]) {
  const actionIdMap = Object.fromEntries(
    actions.map((action) => [action.id, action.id])
  );
  const normalizeGroup = createMouseFollowGroupNormalizer<TAction>(
    (sourceGroupId) => sourceGroupId,
    actionIdMap
  );

  return (action: TAction, axis?: MouseFollowAxis | null): TAction => {
    const normalized = normalizeGroup(action);
    if (!axis) return normalized;

    return updateMouseFollowActionConfig(normalized, (config) =>
      config.axis ? config : {...config, axis}
    );
  };
}

export const forTestSuite = {
  createMouseFollowActionGroupRemapper,
};
